import { Row } from '@tanstack/react-table'
import { mapReleaseDate } from '../../../graphql/schema/queries/helpers/library'
import { BugTracker } from '../../../components/bug-tracker'
import {
  LibraryAgentLanguage,
  LibraryAgentLanguageValue
} from '../components/library/library-agent-language'
import { Vulnerability } from '../../../components/shared'
import { Library as ExportLibrary } from '../components/actions/action-list'
import { getLibrary } from '../api/graphql/helpers'
import { LibraryAssessGrid } from './libraries-interfaces'
import { Library } from './ui-interfaces'
import { SelectedFilters } from '../components/filters/types'

export const getMappedLibrary = async (
  organizationID: string,
  lib: LibraryAssessGrid
) => {
  if (lib) {
    const vulns: Vulnerability[] = lib.vulns.map(
      ({
        name,
        description,
        authentication,
        references,
        visible,
        access_vector,
        access_complexity,
        availability_impact,
        confidentiality_impact,
        integrity_impact,
        severity_value,
        severity_code,
        has_cve,
        has_cvss3_score,
        cvss_3_vector,
        cvss_3_severity_value,
        cvss_3_severity_code,
        epss_score,
        epss_percentile,
        cisa,
        cvss_score_source
      }) => {
        const vuln: Vulnerability = {
          name,
          description,
          authentication,
          references,
          visible,
          accessVector: access_vector,
          accessComplexity: access_complexity,
          availabilityImpact: availability_impact,
          confidentialityImpact: confidentiality_impact,
          integrityImpact: integrity_impact,
          severityValue: parseFloat(severity_value.toFixed(1)),
          severityCode: severity_code,
          hasCve: has_cve,
          hasCvss3: has_cvss3_score,
          cvss3Vector: cvss_3_vector,
          cvss3SeverityValue: cvss_3_severity_value,
          cvss3SeverityCode: cvss_3_severity_code,
          epssScore: epss_score,
          epssPercentile: epss_percentile,
          cisa,
          cvssScoreSource: cvss_score_source
        }

        return vuln
      }
    )

    const { library }: { library: Library } = await getLibrary(
      organizationID,
      lib.app_language,
      lib.hash
    )

    const mappedLibrary = {
      grade: { score: lib.score, value: lib.grade },
      libraryClassUsageCounts: lib.library_class_usage_counts,
      app_language: lib.app_language,
      vulnerabilities: vulns,
      servers: library.servers,
      applications: lib.apps
        ? lib.apps.map(
            ({
              app_id: appId,
              name,
              app_library_status: appLibraryStatus
            }) => ({
              id: `${appId}|${lib.hash}`,
              appId,
              name,
              appLibraryStatus
            })
          )
        : [],
      mergedApplications: lib.mergedApps
        ? lib.mergedApps.map(
            ({ app_id: appId, name, app_library_status: appLibraryStatus }) => {
              return {
                id: `${appId}|${library.hash}`,
                appId,
                name,
                appLibraryStatus
              }
            }
          )
        : [],
      name: lib.file_name,
      hash: lib.hash,
      language: LibraryAgentLanguage.fromValue(
        lib.app_language as LibraryAgentLanguageValue
      ),
      ossEnabled: lib.ossEnabled,
      tags: library.tags ? library.tags : [],
      bugTrackers: normalizeBugTrackers(lib.bugtracker_tickets),
      versions: {
        latest: {
          version: lib.latest_version,
          releaseDate: mapReleaseDate(lib.latest_release_date)
        },
        current: {
          version: lib.file_version,
          releaseDate: mapReleaseDate(lib.release_date)
        }
      },
      policies: {
        invalidVersion: lib.invalid_version,
        restrictedByOrg: lib.restricted,
        restrictedLicense: lib.licenseViolation,
        versionRestriction: lib.version_restriction
      },
      remediationGuidance: lib?.remediationGuidance
        ? {
            minUpgrade: {
              releaseDate: lib.remediationGuidance.minUpgrade.releaseDate,
              version: lib.remediationGuidance.minUpgrade.version,
              grade: lib.remediationGuidance.minUpgrade.grade,
              versionsBehind: lib.remediationGuidance.minUpgrade.versionsBehind,
              score: lib.remediationGuidance.minUpgrade.score
            },
            maxUpgrade: {
              releaseDate: lib.remediationGuidance.maxUpgrade.releaseDate,
              version: lib.remediationGuidance.maxUpgrade.version,
              grade: lib.remediationGuidance.maxUpgrade.grade,
              versionsBehind: lib.remediationGuidance.maxUpgrade.versionsBehind,
              score: lib.remediationGuidance.maxUpgrade.score
            }
          }
        : undefined,
      licenses: lib.licenses,
      usage: {
        totalUsed: lib.classes_used,
        total: lib.class_count
      },
      id: `${lib.hash}|${lib.app_language}`,
      group: library.group,
      custom: library.custom,
      privateLibrary: library.privateLibrary
    }
    return mappedLibrary
  }
}

export const mapExportLibraries = (
  rows: Row<LibraryAssessGrid>[]
): ExportLibrary[] => {
  return rows.map(({ original: lib }) => {
    return {
      grade: { score: lib.score, value: lib.grade },
      libraryClassUsageCounts: [],
      app_language: lib.app_language,
      applications: lib.apps
        ? lib.apps.map(
            ({
              app_id: appId,
              name,
              app_library_status: appLibraryStatus
            }) => ({
              id: `${appId}|${lib.hash}`,
              appId,
              name,
              appLibraryStatus
            })
          )
        : [],
      name: lib.file_name,
      hash: lib.hash,
      language: LibraryAgentLanguage.fromValue(
        lib.app_language as LibraryAgentLanguageValue
      ),
      ossEnabled: lib.ossEnabled,
      tags: lib.tags ? lib.tags : [],
      id: `${lib.hash}|${lib.app_language}`
    }
  })
}

export const getParamValue = (
  key: string,
  queryParameters: Map<string, string[]>,
  isArray: boolean = false
): string | string[] => {
  const param = queryParameters.get(key)

  if (param) {
    if (isArray) {
      return param[0].split(',')
    }
    return param[0]
  }

  return ''
}

export const getInitialFilterValuesFromParams = (
  filters: string[],
  queryParameters: Map<string, string[]>
) => {
  const updatedFilters: SelectedFilters = {}

  filters.forEach((filterKey) => {
    // url query params are case sensitive in RouteProvider
    const values = [
      ...getParamValue(filterKey.toLocaleLowerCase(), queryParameters, true)
    ]

    if (values) {
      values.forEach((value) => {
        if (value) {
          const selectedFilter = {
            key: `${filterKey}.${value}`,
            label: value,
            parentKey: filterKey,
            children: [],
            value
          }
          if (!updatedFilters[filterKey]) {
            updatedFilters[filterKey] = [selectedFilter]
          } else {
            updatedFilters[filterKey].push(selectedFilter)
          }
        }
      })
    }
  })

  return updatedFilters
}

export const normalizeBugTrackers: (bugTrackers: any[]) => BugTracker[] = (
  bugTrackers: any[]
) =>
  bugTrackers.map(
    ({
      bugtracker_name: name,
      bugtracker_type: type,
      creation_time: creation,
      ticket_key: ticketKey,
      ticket_title: ticketTitle,
      ticket_url: ticketUrl
    }) => {
      return {
        name,
        type,
        creation,
        ticketKey,
        ticketTitle,
        ticketUrl
      }
    }
  ) || []
